import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
  Typography,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import GetAppIcon from "@material-ui/icons/GetApp";
import { Editor } from "@tinymce/tinymce-react";
import RichTextEditor, {
  documentPreset,
} from "Components/RichTextEditor";
import { getPreviewNotice, updatePreviewNoticeDocument } from "../../service";
import FormError from "../FormError";
import FullscreenToggle from "../FullscreenToggle";
import useEditorFeatures, {
  EditorStatus,
  printDocument,
  useProofread,
} from "../EditorFeatures";
import Swal from "sweetalert2";
import {
  sanitizeNoticeHtml,
  splitLetterhead,
  joinNoticeHtml,
  buildNoticeEditorInit,
  NOTICE_TOOLBAR,
  noticeBodyCss,
  NOTICE_BODY_CLASS,
  NOTICE_PAGE_WIDTH,
  NOTICE_MARGIN_X,
  NOTICE_MARGIN_TOP,
  NOTICE_MARGIN_BOTTOM,
  NOTICE_FONT_STACK,
  NOTICE_LETTERHEAD_FONT_SIZE,
  NOTICE_LOGO_WIDTH,
  isHtmlBlank,
} from "./noticeHtml";
import { TINYMCE_API_KEY } from "../../tinymceConfig";

// After saving, re-GET the preview so the admin sees what actually survived the
// HTML -> docx -> HTML round trip. Off until the backend confirms that
// GET /bac/preview-notice/{file_id}/ returns the STAGED file: if it regenerates
// from the template instead, this reload would replace the edits we just saved
// with template text. Flip to true once that is confirmed.
const RELOAD_AFTER_SAVE = false;

// Which editor this dialog mounts. Flip to false to go back to TinyMCE - both
// stay installed until every call site has moved off it. A constant rather
// than an env var on purpose: the bundle is built locally and copied into the
// image, so the value is baked in at build time either way and a var would
// only be one more thing to remember. See TIPTAP-IMPLEMENTATION-GUIDE.md.
const USE_TIPTAP = true;

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    height: "92vh",
    maxHeight: "92vh",
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    padding: "1.5rem",
    backgroundColor: "#f1f1f4",
    scrollbarGutter: "stable",
  },
  previewContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    width: "100%",
    maxWidth: NOTICE_PAGE_WIDTH,
    margin: "0 auto",
  },
  fillHeight: {
    flex: "1 1 auto",
    minHeight: 0,
  },








































































































  page: {
    backgroundColor: "#fff",
    border: "1px solid #d8d8d8",
    width: NOTICE_PAGE_WIDTH,
    maxWidth: "100%",
    margin: "0 auto",
    padding: 0,
    boxShadow: "0 1px 4px rgba(0,0,0,0.14)",
  },
  pageEditing: {
    display: "flex",
    flexDirection: "column",
  },
  editorShell: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    minHeight: "320px",
    // TinyMCE writes a pixel height onto its own container; as a flex item that
    // is only the flex basis, so the editor grows into the dialog on a tall
    // screen and shrinks on a short one instead of forcing a second scrollbar.
    //
    // Its own border and rounded corners are dropped so the editor reads as the
    // sheet itself rather than as a widget dropped onto it - and so the text
    // inside starts exactly one page margin in, as in read mode.
    "& .tox-tinymce": {
      flex: "1 1 auto",
      border: "none",
      borderTop: "1px solid #e0e0e0",
      borderRadius: 0,
    },
    // The same treatment for Tiptap. It has no inner iframe, so the scrolling
    // element is the content div itself rather than the editor shell.
    "& .rte": {
      display: "flex",
      flexDirection: "column",
      flex: "1 1 auto",
      minHeight: 0,
      border: "none",
      borderTop: "1px solid #e0e0e0",
      borderRadius: 0,
    },
    "& .rte__content": {
      flex: "1 1 auto",
      minHeight: 0,
      overflowY: "auto",
    },
  },
  letterhead: {
    position: "relative",
    padding: `${NOTICE_MARGIN_TOP} ${NOTICE_MARGIN_X} 0`,
    userSelect: "none",
    fontFamily: NOTICE_FONT_STACK,
    fontSize: NOTICE_LETTERHEAD_FONT_SIZE,
    lineHeight: 1.25,
    color: TEXT.strong,
    "& img": { width: NOTICE_LOGO_WIDTH, maxWidth: "100%", height: "auto" },
    "& table": { width: "100%", borderCollapse: "collapse", maxWidth: "100%", margin: 0 },
    "& td, & th": { border: "none", padding: 0, verticalAlign: "middle" },
    "& p": { margin: 0 },
    "& a": { color: TEXT.link, textDecoration: "underline" },
  },
  letterheadRule: {
    borderBottom: "1.5pt solid #000",
    paddingBottom: "2pt",
  },
  lockedChip: {
    position: "absolute",
    top: 8,
    right: 8,
    height: "22px",
    fontSize: "0.7rem",
    color: TEXT.muted,
    backgroundColor: "#f5f5f5",
  },
  editedChip: {
    height: "24px",
    fontSize: "0.75rem",
    color: TEXT.brand,
    backgroundColor: "#f3ecf6",
  },
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "2rem",
  },
  actionArea: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    padding: "0.75rem 1.5rem",
    borderTop: "1px solid #eee",
  },
  actionInfo: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  actionGroup: {
    display: "flex",
    gap: "10px",
  },
  hint: {
    fontSize: "0.75rem",
    color: TEXT.muted,
  },
}));

const PREVIEW_BODY_CSS = noticeBodyCss(`.${NOTICE_BODY_CLASS}`);

// Everything the shared editor offers a notice - see
// Components/RichTextEditor/features.js.
const NOTICE_FEATURES = {
  pageBreakButtons: true,
  typingShortcuts: true,
  dictation: true,
  saveShortcut: true,
  printShortcut: true,
  wordCount: true,
  justifyIndent: true,
};

// The printout: the notice's own body stylesheet on an A4 sheet with the
// .docx's margins (so the body loses its on-screen padding), the letterhead as
// the preview draws it, and page breaks as real breaks rather than the dashed
// marker the scrolling view uses.
const PRINT_CSS = `
  @page { size: A4; margin: ${NOTICE_MARGIN_TOP} ${NOTICE_MARGIN_X} ${NOTICE_MARGIN_BOTTOM}; }
  html, body { margin: 0; background: #fff; }
  .notice-print-letterhead {
    font-family: ${NOTICE_FONT_STACK};
    font-size: ${NOTICE_LETTERHEAD_FONT_SIZE};
    line-height: 1.25;
    border-bottom: 1.5pt solid #000;
    padding-bottom: 2pt;
  }
  .notice-print-letterhead img { width: ${NOTICE_LOGO_WIDTH}; max-width: 100%; height: auto; }
  .notice-print-letterhead table { width: 100%; border-collapse: collapse; margin: 0; }
  .notice-print-letterhead td, .notice-print-letterhead th { border: none; padding: 0; vertical-align: middle; }
  .notice-print-letterhead p { margin: 0; }
  .notice-print-letterhead a { color: #0000ff; text-decoration: underline; }
${noticeBodyCss(`.${NOTICE_BODY_CLASS}`)}
  .${NOTICE_BODY_CLASS} { padding: 12pt 0 0; }
  .${NOTICE_BODY_CLASS} p[style*="page-break-before"] {
    border-top: 0;
    margin-top: 0;
    padding-top: 0;
  }
`;

// TinyMCE applied this stylesheet inside its iframe via content_style. Tiptap
// renders into the page, so the same rules have to be scoped to the editor's
// own content element instead - otherwise the notice is laid out over a 7.3in
// page column when read and over the raw dialog width when edited.
const TIPTAP_BODY_CSS = `
  .rte--notice .rte__content { padding: 0; }
${noticeBodyCss(".rte--notice .ProseMirror")}`;

export default function NoticePreview({
  open,
  onClose,
  previewData,
  disputeId,
  listingDate,
  noticeType,
  // The per-type inputs the notice was generated from (filing dates, Fee
  // Schedule overrides), already shaped as query params by the Create Notice
  // screen. Passed straight back on reload so a re-fetch regenerates the same
  // notice instead of one with blank deadlines.
  noticeInputParams,
  onUpdateSuccess,
  // The case's number ("AC No. 12/2026"), printed at the top of each page the
  // editor's Page break / New page adds.
  caseNumber,
}) {
  const classes = useStyles();

  const [editMode, setEditMode] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [savingDoc, setSavingDoc] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [savedOnce, setSavedOnce] = useState(false);

  const editorRef = useRef(null);

  // Local state for the notice, initialised whenever previewData changes.
  const [fileId, setFileId] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [contentHtml, setContentHtml] = useState("");
  const [manuallyEdited, setManuallyEdited] = useState(false);
  // TinyMCE reads initialValue once, so the editor is keyed and remounted
  // whenever the underlying content is replaced.
  const [editorVersion, setEditorVersion] = useState(0);
  // RichTextEditor is controlled, unlike TinyMCE's uncontrolled initialValue.
  // The draft has to be held here or the editor's content-sync effect would
  // see `value` disagree with what the user typed and reset the caret.
  const [bodyDraft, setBodyDraft] = useState("");

  useEffect(() => {
    if (!previewData) return;
    setFileId(previewData.fileId || null);
    setDownloadUrl(previewData.downloadUrl || "");
    setContentHtml(previewData.contentHtml || "");
    setManuallyEdited(Boolean(previewData.manuallyEdited));
    setEditMode(false);
    setDirty(false);
    setSavedOnce(false);
    setSaveError("");
    setEditorVersion((version) => version + 1);
  }, [previewData]);

  const editableAvailable = Boolean(contentHtml);

  // The letterhead is peeled off client-side and never enters the editor.
  // rawHeaderHtml is unsanitised on purpose - it is what gets re-attached on save.
  const { headerHtml: rawHeaderHtml, bodyHtml: rawBodyHtml } = useMemo(
    () => splitLetterhead(contentHtml),
    [contentHtml]
  );

  const safeHeaderHtml = useMemo(
    () => sanitizeNoticeHtml(rawHeaderHtml, { allowImages: true }),
    [rawHeaderHtml]
  );

  // When the split found no letterhead the body still contains it, so keep the
  // header allowances rather than stripping the logo out of the notice.
  const headerInBody = !rawHeaderHtml;
  const safeBodyHtml = useMemo(
    () => sanitizeNoticeHtml(rawBodyHtml, { allowImages: headerInBody }),
    [rawBodyHtml, headerInBody]
  );

  // safeBodyHtml only changes when a different notice loads or a save comes
  // back, never while typing - so this does not fight the user's edits.
  useEffect(() => {
    setBodyDraft(safeBodyHtml);
  }, [safeBodyHtml]);

  const editorInit = useMemo(() => buildNoticeEditorInit(), []);

  const resetAndClose = useCallback(() => {
    setEditMode(false);
    setDirty(false);
    setSaveError("");
    onClose();
  }, [onClose]);

  // Unsaved edits used to disappear silently on close - confirm first.
  const handleClose = async () => {
    if (!dirty) {
      resetAndClose();
      return;
    }

    const result = await Swal.fire({
      title: "Discard changes?",
      text: "Your edits to this notice have not been saved. They will be lost.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Discard",
      cancelButtonText: "Keep editing",
      reverseButtons: true,
    });

    if (result.isConfirmed) resetAndClose();
  };

  const markDirty = () => setDirty(true);

  const handleEnterEditMode = () => {
    setSaveError("");
    setEditMode(true);
  };

  // Re-fetch the staged notice so the editor shows what actually persisted
  // through the HTML -> docx -> HTML conversion. See RELOAD_AFTER_SAVE above.
  const reloadFromServer = useCallback(
    async (id) => {
      try {
        setReloading(true);
        const res = await getPreviewNotice(
          {
            dispute_id: disputeId,
            listing_date: listingDate,
            notice_type: noticeType,
            ...(noticeInputParams || {}),
          },
          id
        );
        const item = res?.data?.data?.[0] || res?.data?.[0];
        if (item && item.content_html) {
          setContentHtml(item.content_html);
          if (item.download_url) setDownloadUrl(item.download_url);
          setManuallyEdited(Boolean(item.manually_edited));
          setEditorVersion((version) => version + 1);
        }
      } catch (err) {
        // The save already succeeded - a failed reload is not worth an error
        // banner, the admin is looking at their own saved content either way.
        console.error("Error reloading notice preview:", err);
      } finally {
        setReloading(false);
      }
    },
    [disputeId, listingDate, noticeType, noticeInputParams]
  );

  // POST the whole document: pristine letterhead + edited body. The backend
  // rebuilds the .docx and flags the staged file as manually edited, so Send
  // Notice dispatches this text rather than a fresh render of the template.
  // Resolves to the saved file's download URL, or null if nothing was saved.
  const handleSaveDocument = async () => {
    if (!fileId) return null;
    if (!USE_TIPTAP && !editorRef.current) return null;

    const editedBody = USE_TIPTAP ? bodyDraft : editorRef.current.getContent();
    if (isHtmlBlank(editedBody)) {
      setSaveError("The notice body cannot be empty.");
      return null;
    }

    setSaveError("");
    setSavingDoc(true);
    try {
      const content = joinNoticeHtml(
        rawHeaderHtml,
        sanitizeNoticeHtml(editedBody, { allowImages: headerInBody })
      );

      const res = await updatePreviewNoticeDocument(fileId, content);
      const updatedItem = res?.data?.data?.[0] || res?.data?.[0];

      if (updatedItem) {
        const nextFileId = updatedItem.file_id || fileId;
        setFileId(nextFileId);
        if (updatedItem.download_url) setDownloadUrl(updatedItem.download_url);
        setManuallyEdited(true);
        setDirty(false);
        setSavedOnce(true);
        setEditMode(false);

        // Show the saved version, from the response where the backend echoes it.
        setContentHtml(updatedItem.content_html || content);
        setEditorVersion((version) => version + 1);

        // Hand the file_id back so Send Notice dispatches the edited file.
        if (onUpdateSuccess) onUpdateSuccess(nextFileId);

        if (RELOAD_AFTER_SAVE) await reloadFromServer(nextFileId);

        // Stay open: the admin should see the saved version before sending.
        Swal.fire(
          "Success",
          "Your changes to the notice have been saved.",
          "success"
        );
        return updatedItem.download_url || downloadUrl;
      } else {
        setSaveError("Failed to parse the save response.");
      }
    } catch (err) {
      console.error("Error saving notice document:", err);
      setSaveError("An error occurred while saving your edits.");
    } finally {
      setSavingDoc(false);
    }
    return null;
  };

  const busy = savingDoc || reloading;

  // Print what is on screen: the letterhead and the body as edited so far.
  const handlePrint = () => {
    const body = editMode ? bodyDraft : safeBodyHtml;
    const html =
      (safeHeaderHtml ? `<div class="notice-print-letterhead">${safeHeaderHtml}</div>` : "") +
      `<div class="${NOTICE_BODY_CLASS}">${body}</div>`;
    printDocument({
      html,
      css: PRINT_CSS,
      title: caseNumber ? `Notice ${caseNumber}` : "Notice",
    }).catch((err) => {
      console.error("Failed to open the print window:", err);
      setSaveError("Could not open the print window. Please try again.");
    });
  };

  // The .docx of what is on screen: unsaved edits are saved first, as the
  // order sheet's Download does.
  const handleDownload = async () => {
    const url = dirty ? await handleSaveDocument() : downloadUrl;
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const proofread = useProofread({
    disputeId,
    getHtml: () => bodyDraft,
    setHtml: (html) => {
      setBodyDraft(html);
      markDirty();
    },
  });

  const { editorProps, dictation } = useEditorFeatures({
    features: NOTICE_FEATURES,
    preset: documentPreset,
    toolbar: NOTICE_TOOLBAR,
    caseNumber,
    disputeId,
    // Ctrl+S: what Save Document Changes does, with its guards.
    onSave: () => {
      if (editMode && !busy) handleSaveDocument();
    },
    onPrint: handlePrint,
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullscreen}
      classes={fullscreen ? undefined : { paper: classes.dialogPaper }}
      disableBackdropClick={dirty}
      disableEnforceFocus
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" style={{ gap: "10px" }}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Notice Preview
            </Typography>
            {manuallyEdited && (
              <Chip
                className={classes.editedChip}
                size="small"
                icon={<EditOutlinedIcon style={{ fontSize: "0.9rem" }} />}
                label="Manually edited - this version will be sent"
              />
            )}
          </Box>
          <Box display="flex" alignItems="center" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
            {editMode && (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  disabled={proofread.state === "loading" || busy}
                  onClick={proofread.run}
                  style={{ textTransform: "none", minWidth: "110px" }}
                >
                  {proofread.state === "loading" ? (
                    <Box display="flex" alignItems="center" style={{ gap: "0.4rem" }}>
                      <CircularProgress size={13} color="inherit" />
                      <span>Reviewing...</span>
                    </Box>
                  ) : (
                    "AI Proofread"
                  )}
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  disabled={busy}
                  onClick={handleSaveDocument}
                  style={{ textTransform: "none" }}
                >
                  Save Draft
                </Button>
              </>
            )}
            {(downloadUrl || editMode) && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                disabled={busy}
                onClick={handleDownload}
                style={{ textTransform: "none" }}
              >
                Download
              </Button>
            )}
            {editableAvailable && (
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={handlePrint}
                style={{ textTransform: "none" }}
              >
                Print
              </Button>
            )}
            <FullscreenToggle
              fullscreen={fullscreen}
              onToggle={() => setFullscreen((value) => !value)}
            />
            <Button variant="outlined" size="small" onClick={handleClose}>
              Close
            </Button>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers className={classes.dialogContent}>
        <style dangerouslySetInnerHTML={{ __html: PREVIEW_BODY_CSS }} />
        {USE_TIPTAP && (
          <style dangerouslySetInnerHTML={{ __html: TIPTAP_BODY_CSS }} />
        )}

        <Box
          className={`${classes.previewContainer} ${
            editMode ? classes.fillHeight : ""
          }`}
        >
          {reloading && (
            <Box className={classes.loaderContainer}>
              <CircularProgress size={24} />
            </Box>
          )}

          {editableAvailable ? (
            <Box
              className={`${classes.page} ${
                editMode ? `${classes.pageEditing} ${classes.fillHeight}` : ""
              }`}
            >
              {/* Letterhead: rendered from the notice, never editable. */}
              {safeHeaderHtml && (
                <Box className={classes.letterhead} contentEditable={false}>
                  <div
                    className={classes.letterheadRule}
                    dangerouslySetInnerHTML={{ __html: safeHeaderHtml }}
                  />
                  <Chip
                    className={classes.lockedChip}
                    size="small"
                    icon={<LockOutlinedIcon style={{ fontSize: "0.85rem" }} />}
                    label="Letterhead"
                  />
                </Box>
              )}

              {editMode ? (
                <Box className={classes.editorShell}>
                  {USE_TIPTAP ? (
                    <RichTextEditor
                      // Keyed on the notice only. TinyMCE needed editorVersion
                      // in the key because initialValue is read once; Tiptap
                      // picks up replaced content through `value`.
                      key={fileId || "notice"}
                      className="rte--notice"
                      value={bodyDraft}
                      onChange={(html) => {
                        setBodyDraft(html);
                        markDirty();
                      }}
                      {...editorProps}
                      minHeight={0}
                      onReady={(editor) => {
                        editorRef.current = editor;
                      }}
                    />
                  ) : (
                    <Editor
                      key={`${fileId || "notice"}-${editorVersion}`}
                      apiKey={TINYMCE_API_KEY}
                      onInit={(evt, editor) => {
                        editorRef.current = editor;
                      }}
                      initialValue={safeBodyHtml}
                      init={editorInit}
                      onDirty={markDirty}
                    />
                  )}
                </Box>
              ) : (
                <div
                  className={NOTICE_BODY_CLASS}
                  dangerouslySetInnerHTML={{ __html: safeBodyHtml }}
                />
              )}
            </Box>
          ) : (
            <Box className={classes.loaderContainer}>
              <Typography color="textSecondary">
                {downloadUrl
                  ? "This notice could not be loaded for editing. Download the .docx to review it."
                  : "No preview document available."}
              </Typography>
            </Box>
          )}

          {/* --- Structured recipients editor (disabled, see note at top) ---
          {showRecipients && (
            <Box mt={1} ref={recipientsRef}>
              <NoticeRecipientsEditor
                claimants={claimants}
                counsels={counsels}
                onDeleteClaimant={handleDeleteClaimant}
                onDeleteCounsel={handleDeleteCounsel}
                onAddClaimant={handleAddClaimant}
                onAddCounsel={handleAddCounsel}
              />
            </Box>
          )}
          --------------------------------------------------------------- */}

          <FormError message={saveError} />
        </Box>
      </DialogContent>

      <DialogActions className={classes.actionArea}>
        <Box className={classes.actionInfo}>
          {downloadUrl && (
            <Button
              size="small"
              startIcon={<GetAppIcon />}
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download .docx
            </Button>
          )}
          <EditorStatus
            dictation={dictation}
            proofread={proofread}
            fallback={
              editMode && safeHeaderHtml ? (
                <Typography className={classes.hint} component="span">
                  The letterhead is generated and cannot be typed over.
                </Typography>
              ) : null
            }
          />
          {!editMode && savedOnce && !RELOAD_AFTER_SAVE && (
            <Typography className={classes.hint} component="span">
              Saved. Download the .docx to confirm the formatting.
            </Typography>
          )}
        </Box>

        <Box className={classes.actionGroup}>
          {!editMode && editableAvailable && (
            <Button color="primary" variant="outlined" onClick={handleEnterEditMode} size="small">
              Edit
            </Button>
          )}
          {editMode && (
            <>
              <Button variant="outlined" disabled={busy} onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={busy}
                onClick={handleSaveDocument}
                size="small"
              >
                {busy ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  "Save Document Changes"
                )}
              </Button>
            </>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
